import { useState } from "react";
import { Link } from "react-router-dom";
import { LayoutDashboard, X, ArrowRight } from "lucide-react";
import useInstallPrompt from "@/hooks/useInstallPrompt";

/**
 * DashboardShortcutCard: small card on the Dashboard that lets owners
 * install/add a home-screen shortcut that opens straight to the
 * business dashboard. No instructional modals: just the install
 * button (when the browser exposes a prompt) plus an "already
 * installed → Open Dashboard" state. Dismissible per device.
 */
export default function DashboardShortcutCard() {
  const { canInstall, installed, promptInstall } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("confirmph_dashboard_shortcut_dismissed") === "1"; }
    catch (_e) { return false; }
  });

  if (dismissed) return null;

  const dismiss = () => {
    try { localStorage.setItem("confirmph_dashboard_shortcut_dismissed", "1"); }
    catch (_e) { /* ignore */ }
    setDismissed(true);
  };

  // ---- already installed / running standalone ------------------------
  if (installed) {
    return (
      <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-4"
                data-testid="dashboard-shortcut-card">
        <div className="flex items-start gap-3">
          <LayoutDashboard className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-1" />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-display font-semibold text-emerald-900"
                  data-testid="dashboard-shortcut-title">
                ConfirmPH is installed
              </h3>
              <button onClick={dismiss}
                      data-testid="dashboard-shortcut-dismiss"
                      aria-label="Dismiss"
                      className="text-emerald-700/60 hover:text-emerald-900 -mr-1 -mt-1">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-2">
              <Link to="/app" data-testid="dashboard-shortcut-open"
                     className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg btn-brand text-sm font-medium">
                Open Dashboard <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ---- install prompt not available → hide the card silently ---------
  if (!canInstall) return null;

  // ---- install available → show the button ---------------------------
  return (
    <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-4"
              data-testid="dashboard-shortcut-card">
      <div className="flex items-start gap-3">
        <LayoutDashboard className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-1" />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-display font-semibold text-emerald-900"
                data-testid="dashboard-shortcut-title">
              Add Dashboard Shortcut
            </h3>
            <button onClick={dismiss}
                    data-testid="dashboard-shortcut-dismiss"
                    aria-label="Dismiss"
                    className="text-emerald-700/60 hover:text-emerald-900 -mr-1 -mt-1">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-emerald-900/80 mt-0.5 max-w-xl">
            Add ConfirmPH Dashboard to your phone home screen so owners and
            admins can open the business dashboard directly.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button onClick={promptInstall}
                    data-testid="dashboard-shortcut-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg btn-brand text-sm font-medium">
              Add Dashboard Shortcut
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
